import _ from 'lodash';

import {
  BaseNode,
  Field,
  isOptionField,
  OptionField,
  FieldGroup,
  QuestionnaireDefinition,
  Section,
  SectionGroup,
  SelectOption,
  Subsection,
} from '@zinnia/form-engine-sdk';

/** This makes every property of an object optional, including the properties of child properties */
export type RecursivePartial<T> = {
  [P in keyof T]?: RecursivePartial<T[P]>;
};

export type PartialQuestionnaire = RecursivePartial<QuestionnaireDefinition>;
type PartialSection = RecursivePartial<Section>;
type PartialSectionGroup = RecursivePartial<SectionGroup>;
type PartialFieldGroup = RecursivePartial<FieldGroup>;
type PartialField = RecursivePartial<Field>;
type PartialSelectOption = RecursivePartial<SelectOption>;
type PartialSubsection = RecursivePartial<Subsection>;

export function overrideQuestionnaireProperties(
  questionnaire: QuestionnaireDefinition,
  updatedQuestionnaire: PartialQuestionnaire,
): QuestionnaireDefinition {
  validateAllOverriddenNodesExist(questionnaire, _.compact(updatedQuestionnaire));

  const originalQuestionnaire = _.cloneDeep(questionnaire);

  _.forEach(originalQuestionnaire, (sectionGroup) => {
    const updatedSectionGroup = updatedQuestionnaire?.find(
      (updatedSectionGroup) => sectionGroup.id === updatedSectionGroup?.id,
    );
    if (!updatedSectionGroup) return;
    overrideSectionGroup(sectionGroup, updatedSectionGroup);
  });

  return originalQuestionnaire;
}

export function overrideSectionGroup(
  sectionGroup: SectionGroup,
  updatedSectionGroup: PartialSectionGroup,
): SectionGroup {
  validateAllOverriddenNodesExist(sectionGroup.sections, _.compact(updatedSectionGroup.sections));

  const updatedSectionGroupWithoutSections = _.omit(updatedSectionGroup, 'sections');

  const overriddenSectionGroup = _.merge(sectionGroup, updatedSectionGroupWithoutSections);

  _.forEach(sectionGroup.sections, (section) => {
    const updatedSection = updatedSectionGroup.sections?.find((updatedSection) => section.id === updatedSection?.id);

    if (!updatedSection) return;
    overrideSection(section, updatedSection);
  });

  return overriddenSectionGroup;
}

export function overrideSection(section: Section, updatedSection: PartialSection): Section {
  validateAllOverriddenNodesExist(section.subsections, _.compact(updatedSection.subsections));

  const updatedSectionWithoutSubsections = _.omit(updatedSection, 'subsections');
  const overriddenSection = _.merge(section, updatedSectionWithoutSubsections);

  _.forEach(section.subsections, (subsection) => {
    const updatedSubsection = updatedSection.subsections?.find(
      (updatedSubsection) => subsection.id === updatedSubsection?.id,
    );

    if (!updatedSubsection) return;
    overrideSubsection(subsection, updatedSubsection);
  });

  return overriddenSection;
}

export function overrideSubsection(subsection: Subsection, updatedSubsection: PartialSubsection): Subsection {
  validateAllOverriddenNodesExist(subsection.fieldGroups, _.compact(updatedSubsection.fieldGroups));

  const updatedSubsectionWithoutQuestions = _.omit(updatedSubsection, 'fieldGroups');
  const overriddenSubsection = _.merge(subsection, updatedSubsectionWithoutQuestions);

  _.forEach(subsection.fieldGroups, (fieldGroup) => {
    const updatedFieldGroup = updatedSubsection.fieldGroups?.find(
      (updatedFieldGroup) => fieldGroup.id === updatedFieldGroup?.id,
    );

    if (!updatedFieldGroup) return;
    overrideQuestion(fieldGroup, updatedFieldGroup);
  });

  return overriddenSubsection;
}

export function overrideQuestion(fieldGroup: FieldGroup, updatedFieldGroup: PartialFieldGroup): FieldGroup {
  validateAllOverriddenNodesExist(fieldGroup.fields, _.compact(updatedFieldGroup.fields));

  const updatedQuestionWithoutFields = _.omit(updatedFieldGroup, 'fields');
  const overriddenQuestion = _.merge(fieldGroup, updatedQuestionWithoutFields);

  _.forEach(fieldGroup.fields, (field) => {
    const updatedField = updatedFieldGroup.fields?.find((updatedField) => field.id === updatedField?.id);

    if (!updatedField) return;
    overrideField(field, updatedField);
  });

  return overriddenQuestion;
}

export function overrideField(field: Field, updatedField: PartialField): Field {
  const updatedFieldWithoutOptions = _.omit(updatedField, 'options');
  const overriddenField = _.merge(field, updatedFieldWithoutOptions);

  const updatedOptionField = updatedField as OptionField;

  if (isOptionField(field) && updatedOptionField.options) {
    validateAllOverriddenNodesExist(field.options, _.compact(updatedOptionField.options) as PartialSelectOption[]);

    _.forEach(field.options, (option) => {
      const updatedOption = updatedOptionField.options?.find((updatedOption) => option.id === updatedOption?.id);

      if (!updatedOption) return;

      _.merge(option, updatedOption);
    });
  }

  return overriddenField;
}

function validateAllOverriddenNodesExist<TNode extends BaseNode>(
  originalNodes: TNode[] | undefined,
  updatedNodes: RecursivePartial<TNode>[],
): void {
  const nonExistingIds: string[] = [];
  const nodesWithoutIds: RecursivePartial<TNode>[] = [];

  updatedNodes.forEach((updatedNode) => {
    if (!updatedNode.id) {
      nodesWithoutIds.push(updatedNode);
      return;
    }

    const originalNode = originalNodes?.find((node) => updatedNode.id === node?.id);

    if (!originalNode) {
      nonExistingIds.push(updatedNode.id as string);
    }
  });

  if (nonExistingIds.length) {
    throw new Error(
      `The updated questionnaire is attempting to override the following non-existing ids: ${nonExistingIds.join(
        ', ',
      )}`,
    );
  }

  if (nodesWithoutIds.length) {
    throw new Error(
      `The updated questionnaire is attempting to override the following nodes without specifying their IDs: ${JSON.stringify(
        nodesWithoutIds,
      )}`,
    );
  }
}
